import React, { useState, useEffect, useCallback } from "react";

import { useFormik } from "formik";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

import { migraPasoGet, migraTipoDePasoGet } from "services/services-mia/migra";
import Modal from "components/UI/Modal/Modal";
import { useService } from "components/route/ServiceRoute";

import XGrid from "components/UI/Grid/XGrid";
import Filters from "components/Filters";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import { Button, HeaderButton, Buttons, Header } from "components";
import { Add, BlockOutlined, Cached, DeleteOutline, EditOutlined } from "@mui/icons-material";

import { DeleteModal, InsertModal, QrModal, StatusModal, UpdateModal } from "./Actions";

import { migraPaso as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";

import s from "styles/pages/Pages.module.scss";

import qrImg from "../../../../assets/icons/DescargarQR.svg"

const statusLabel = (cod) => {
    if (cod === "A") return "Alta";
    if (cod === "B") return "Baja";
    return cod ?? "";
};

export default function Paso() {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [tiposPasoFilter, setTiposPasoFilter] = useState([]);
    const [rowSelected, setRowSelected] = useState({
        status: false,
        row: {},
    });

    const { service } = useService();

    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);

    const loadPasos = useCallback(
        async (filters = {}) => {
            setRowSelected({ status: false, row: {} });
            setIsLoading(true);
            setRows([]);
            const { result } = await migraPasoGet(filters, credentials);
            setRows(
                (result || []).map((item) => {
                    const {
                        migraPasoId,
                        migraPasoUUID,
                        migraPasoCod,
                        migraPasoNombre,
                        gateLugarId,
                        gateLugarNombre,
                        migraTipoDePasoId,
                        migraTipoDePasoNombre,
                        migraPasoStatus,
                        migraPasoVersion,
                        merchantIRurl
                    } = item;

                    return {
                        id: migraPasoId,
                        migraPasoId,
                        migraPasoUUID,
                        migraPasoCod,
                        migraPasoNombre,
                        gateLugarId,
                        gateLugarNombre,
                        migraTipoDePasoId,
                        migraTipoDePasoNombre,
                        migraPasoStatus,
                        migraPasoVersion,
                        merchantIRurl,
                        [titles[0]]: migraPasoNombre,
                        [titles[1]]: migraPasoCod,
                        [titles[2]]: gateLugarNombre,
                        [titles[3]]: migraTipoDePasoNombre,
                        [titles[4]]: statusLabel(migraPasoStatus),
                    };
                })
            );
            setIsLoading(false);
        },
        [credentials]
    );

    const formik = useFormik({
        initialValues: {
            migraPasoNombre: "",
            migraTipoDePasoId: "",
            migraPasoStatus: "",
        },
        onSubmit: (values) => {
            loadPasos(values);
        },
    });

    const resetFilters = () => {
        formik.resetForm();
        loadPasos({});
    };

    useEffect(() => {
        setRoute(Routes.migraPasoTable);
    }, [setRoute]);

    useEffect(() => {
        if (!credentials?.merchantId || !service) return;
        (async () => {
            const { result } = await migraTipoDePasoGet({}, credentials);
            setTiposPasoFilter(result || []);
        })();
    }, [credentials, service]);

    useEffect(() => {
        if (!credentials?.merchantId || !service) return;
        loadPasos({});
    }, [service, credentials, loadPasos]);

    const handleClick = ({ procede, title }) => {
        setModalDetails({ title, procede, isOpen: true });
    };

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    const isBaja = rowSelected.row.migraPasoStatus === "B";

    return (
        <main className={s.main}>
            <Modal
                onClose={closeModal}
                open={modalDetails.isOpen}
                title={modalDetails.title}
            >
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        paso={rowSelected.row}
                        updateList={() => loadPasos(formik.values)}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        paso={rowSelected.row}
                        updateList={() => loadPasos(formik.values)}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        updateList={() => loadPasos(formik.values)}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.QR && (
                    <QrModal
                        paso={rowSelected.row}
                        servicio={service}
                        updateList={() => loadPasos(formik.values)}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DEACTIVATE && (
                    <StatusModal
                        paso={rowSelected.row}
                        updateList={() => loadPasos(formik.values)}
                        closeModal={closeModal}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "text",
                                name: "migraPasoNombre",
                                label: "Nombre",
                                disabled: false,
                            },
                            {
                                type: "select",
                                name: "migraTipoDePasoId",
                                label: "Tipo de paso",
                                disabled: false,
                                options: [
                                    {
                                        migraTipoDePasoId: "",
                                        migraTipoDePasoNombre: "Todos",
                                    },
                                    ...tiposPasoFilter,
                                ],
                                firstOptionKey: "migraTipoDePasoId",
                                firstOptionName: "migraTipoDePasoNombre",
                                firstOptionValue: "migraTipoDePasoId",
                            },
                            {
                                type: "select",
                                name: "migraPasoStatus",
                                label: "Estado",
                                disabled: false,
                                options: [
                                    { value: "", label: "Todos" },
                                    { value: "A", label: "Alta" },
                                    { value: "B", label: "Baja" },
                                ],
                                firstOptionKey: "value",
                                firstOptionName: "label",
                                firstOptionValue: "value",
                            },
                        ]}
                    />
                    <HeaderButton text={"Limpiar filtros"} onClick={() => resetFilters()}>
                        <FilterAltOffRoundedIcon />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={() => loadPasos(formik.values)}
                        disabled={rowSelected.row.id}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Generar QR"}
                        handleClick={handleClick}
                        data={{
                            title: "Generar QR",
                            procede: ACTIONS.QR
                        }}
                        disabled={
                            !rowSelected.row.id ||
                            service.servicioPermiso !== "E"
                        }
                    >
                        <img src={qrImg} style={{ width: "24px" }} alt="qr" />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar paso"}
                        handleClick={handleClick}
                        data={{
                            title: "Agregar paso",
                            procede: ACTIONS.INSERT,
                        }}
                        disabled={
                            rowSelected.row.id || service.servicioPermiso !== "E"
                        }
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text={"Editar paso"}
                        handleClick={handleClick}
                        data={{
                            title: "Editar paso",
                            procede: ACTIONS.UPDATE,
                        }}
                        disabled={
                            !rowSelected.row.id ||
                            service.servicioPermiso !== "E"
                        }
                    >
                        <EditOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={isBaja ? "Dar de Alta" : "Dar de Baja"}
                        handleClick={handleClick}
                        data={{
                            title: isBaja ? "Dar de Alta" : "Dar de Baja",
                            procede: ACTIONS.DEACTIVATE,
                        }}
                        disabled={
                            !rowSelected.row.id ||
                            service.servicioPermiso !== "E"
                        }
                    >
                        <BlockOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Eliminar paso"}
                        handleClick={handleClick}
                        data={{
                            title: "Eliminar paso",
                            procede: ACTIONS.DELETE,
                        }}
                        disabled={
                            !rowSelected.row.id ||
                            service.servicioPermiso !== "E"
                        }
                    >
                        <DeleteOutline />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
